import React, { StrictMode, use, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "../i18n";
import "./styling/index.css";
import App from "./App";
import { Provider } from "react-redux";
import { store } from "./app/store";
import { useAppSelector } from "./app/hooks";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { selectConfig, useConfig, configState } from "./features/config/configSlice";
import { SXAlertsContainer } from "./components/ui-components/SXAlert/SXAlertContainer";
import { SXIntroScreen } from "@common/components/ui-components/SXIntroScreen";
import { SXLoadingMessages } from "@common/components/ui-components/SXLoadingMessages";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { selectUser, selectToken, getUser, useUser } from "./features/user/userSlice";
import { isArguments } from "lodash-es";

const container = document.getElementById("app");

if (container) {
  const root = createRoot(container);

  function Main() {
    const [fetchConfigData] = useConfig();
    const config = useAppSelector(selectConfig);
    const { isAuthenticated, loginWithRedirect } = useAuth0();
    const redriectCallback = (appState: any) => {
      window.location.replace("/");
      // Redirect to the URL stored in localStorage or fallback to home page
    };
    useEffect(() => {
      fetchConfigData();
    }, []);

    return (
      config && (
        <Auth0Provider
          domain={config.REACT_APP_AUTH_DOMAIN}
          clientId={config.REACT_APP_AUTH_CLIENTID}
          useRefreshTokensFallback={false}
          authorizationParams={{
            redirect_uri: window.location.origin + (window.location.origin.startsWith("http:") ? "/callback" : ""),
            audience: config.REACT_APP_AUTH_AUDIANCE,
            scope: config.REACT_APP_AUTH_SCOPE,
          }}
          onRedirectCallback={redriectCallback}
          useRefreshTokens={false}
          cacheLocation="memory"
        >
          <SXAlertsContainer />
          <AuthenticatedRoutes config={config} />
        </Auth0Provider>
      )
    );
  }

  function AuthenticatedRoutes({ config }: { config: any }) {
    const { isAuthenticated, isLoading } = useAuth0();

    const disableUser = useAppSelector((state) => state.uiSlice.disableUser);
    const loadingMessage = useAppSelector((state) => state.uiSlice.loadingMessage);
    const user = useAppSelector(selectUser);
    const { getUser, getToken } = useUser();
    useEffect(() => {
      console.log(disableUser);
    }, [disableUser]);

    useEffect(() => {
      async function fetchData() {
        await getToken(true); // probably sets token in store or context
        await getUser(); // sets user in the Redux store
      }

      fetchData();
    }, []);

    if (!isAuthenticated && !isLoading) {
      return <SXIntroScreen config={config} useauth0={useAuth0} trailTag="Simplex Column" />;
      // Show a loading indicator while the authentication state is being determined
    } else if (isAuthenticated && !isLoading && !disableUser && user) {
      return (
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                !disableUser && user ? <App /> : <SXLoadingMessages errorMsg={loadingMessage} useauth0={useAuth0} />
              }
            />
            <Route
              path="/files/:fileId"
              element={
                !disableUser && user ? <App /> : <SXLoadingMessages errorMsg={loadingMessage} useauth0={useAuth0} />
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      );
    } else {
      return <SXLoadingMessages errorMsg={loadingMessage} useauth0={useAuth0} />;
    }
  }

  root.render(
    <StrictMode>
      <Provider store={store}>
        <Main />
      </Provider>
    </StrictMode>,
  );
}
